import type { UpdateApp } from "@biteasy/contracts";
import { Check, Loader2 } from "lucide-react";
import { Field, InfoLabel, Label } from "@/components/catalyst/Fieldset";
import { InputGroup } from "@/components/catalyst/Input";
import { Switch } from "@/components/catalyst/Switch";
import type { ActiveSection, PlatformsSection } from "../types";

interface AppStatusSectionProps {
	active: ActiveSection;
	platforms: PlatformsSection;
}

export function AppStatusSection({ active, platforms }: AppStatusSectionProps) {
	function togglePlatform(platform: "ios" | "android", checked: boolean) {
		const current = platforms.value ?? [];
		const next = checked
			? Array.from(new Set([...current, platform]))
			: current.filter((p) => p !== platform);
		platforms.update(next as UpdateApp["platforms"]);
	}

	return (
		<div className="flex flex-col gap-x-16 gap-y-6 sm:flex-row sm:items-start">
			<Field>
				<InfoLabel info="Disable this app to immediately stop all attribution and claims.">
					App Status
					{active.isSaving && (
						<Loader2 className="h-4 w-4 animate-spin text-base-content/40" />
					)}
					{active.isSaved && <Check className="h-4 w-4 text-emerald-500" />}
				</InfoLabel>

				<InputGroup>
					<div className="flex items-center gap-3">
						<Switch checked={active.value} onChange={active.update} />
						<span className="text-sm text-base-content/60">
							{active.value ? "Active" : "Inactive"}
						</span>
					</div>
				</InputGroup>
			</Field>

			<Field>
				<InfoLabel info="Choose which platforms this app supports.">
					Platforms
					{platforms.isSaving && (
						<Loader2 className="h-4 w-4 animate-spin text-base-100" />
					)}
					{platforms.isSaved && <Check className="h-4 w-4 text-success" />}
				</InfoLabel>
				<InputGroup>
					<div className="flex items-center gap-6">
						<div className="flex items-center gap-3">
							<Label className="font-normal text-muted-foreground">iOS</Label>
							<Switch
								checked={platforms.value?.includes("ios") ?? false}
								onChange={(checked) => togglePlatform("ios", checked)}
							/>
						</div>
						<div className="flex items-center gap-3">
							<Label className="font-normal text-muted-foreground">
								Android
							</Label>
							<Switch
								checked={platforms.value?.includes("android") ?? false}
								onChange={(checked) => togglePlatform("android", checked)}
							/>
						</div>
					</div>
				</InputGroup>
			</Field>
		</div>
	);
}
